import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { downloadQrArchive } from "@/lib/blockQr";

/** Bulk QR download: checkbox list of panel block definitions, zipped into one file. */
export function BulkQrDialog({ open, onOpenChange, items, projectCode }) {
  const [selected, setSelected] = useState(() => new Set());
  const [format, setFormat] = useState("png");
  const [busy, setBusy] = useState(false);

  // Every open starts with all panel types selected.
  useEffect(() => {
    if (open) setSelected(new Set(items.map((item) => item.name)));
  }, [open, items]);

  const toggle = (name) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  const download = async () => {
    const chosen = items.filter((item) => selected.has(item.name));
    if (!chosen.length) return;
    setBusy(true);
    try {
      await downloadQrArchive(projectCode, chosen, format);
      toast.success(`${chosen.length} QR codes downloaded as ZIP.`);
      onOpenChange(false);
    } catch (error) {
      toast.error(`QR archive failed: ${error.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="qr-bulk-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Panel QR Codes</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid="qr-bulk-all"
            onClick={() => setSelected(new Set(items.map((item) => item.name)))}
            className="mono h-7 rounded-full border border-slate-300 px-3 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
          >
            All
          </button>
          <button
            type="button"
            data-testid="qr-bulk-none"
            onClick={() => setSelected(new Set())}
            className="mono h-7 rounded-full border border-slate-300 px-3 text-[9.5px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
          >
            None
          </button>
          <div className="ml-auto flex gap-1">
            {["png", "svg"].map((fmt) => (
              <button
                key={fmt}
                type="button"
                data-testid={`qr-bulk-format-${fmt}`}
                onClick={() => setFormat(fmt)}
                className={`mono h-7 rounded-full border px-3 text-[9.5px] uppercase tracking-wider transition-colors ${
                  format === fmt
                    ? "border-cyan-500/60 bg-cyan-600/10 text-cyan-700"
                    : "border-slate-300 text-slate-600 hover:border-cyan-400/60 hover:text-cyan-700"
                }`}
              >
                {fmt}
              </button>
            ))}
          </div>
        </div>

        <ul className="okn-scroll max-h-80 space-y-0.5 overflow-y-auto rounded-lg border border-slate-200 p-1.5">
          {items.map((item) => (
            <li key={item.name}>
              <label
                data-testid={`qr-bulk-item-${item.name}`}
                className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-900/5"
              >
                <Checkbox
                  checked={selected.has(item.name)}
                  onCheckedChange={() => toggle(item.name)}
                />
                <span className="mono min-w-0 flex-1 truncate text-[12px] text-slate-800">
                  {item.name}
                </span>
                <span className="mono text-[9.5px] uppercase tracking-wider text-slate-500">
                  {item.count} panels
                </span>
              </label>
            </li>
          ))}
          {items.length === 0 ? (
            <li className="px-2.5 py-3 text-[11.5px] text-slate-500">
              No panel block definitions in this model.
            </li>
          ) : null}
        </ul>

        <button
          type="button"
          data-testid="qr-bulk-download"
          disabled={busy || selected.size === 0}
          onClick={download}
          className="mono flex h-9 items-center justify-center gap-2 rounded-full bg-cyan-600 text-[10.5px] font-medium uppercase tracking-wider text-white transition-colors hover:bg-cyan-700 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <Download size={13} />
          )}
          Download ZIP ({selected.size})
        </button>
      </DialogContent>
    </Dialog>
  );
}
